import ExcelJS, { type Worksheet } from "exceljs";
import type { DateTime } from "luxon";
import type { Department } from "../../contexts/AuthProvider";
import { charsToPx, EXCEL_DEFAULT_FONT_SIZE_PT, EXCEL_DEFAULT_ROW_HEIGHT_PT, roundTripRowHeightPt } from "../../utils/excelToFortuneSheet";
import { type NurseInShift, PrintoutOrientation } from "../../utils/models";
import { lineHeightForFontPt } from "../excelWrapText";
import {
	AGENCY_LINE,
	build530Pages,
	COLUMN_HEADERS,
	COLUMN_WIDTHS,
	DEPARTMENT_LINE,
	FORM_REVISION,
	type Form530Settings,
	type Page530,
	type Row530,
	SUBTITLE,
	TITLE,
	VERIFICATION_TEXT,
} from "./pages";

/**
 * The CDPH 530 as an Excel workbook (APP-645) — one worksheet per `build530Pages` page.
 *
 * This is the ONE rendering of the form. The preview shows it, the xlsx download is it, and the PDF
 * download and Print button are it run through `generatePrintoutPdf` — the same Excel-to-PDF path
 * every other printout in the app uses. It replaced a second, hand-drawn jsPDF layout: two renderers
 * of one state form could not be kept honest with each other, and facilities need cells they can type
 * into anyway for what we don't track (meal breaks above all).
 */

const COLUMN_COUNT = COLUMN_HEADERS.length;
const LAST_COLUMN_LETTER = "F";

/**
 * Points → Excel character-width units, via the ~96dpi pixel width Excel's unit derives from.
 * Deliberately NOT switched to `pxToChars`: this approximation draws the form ~9% narrower than the
 * state's 720pt layout, and correcting it changes what facilities print — an open compliance
 * decision (APP-406), pinned by `form530Pdf.spec.ts` so it cannot drift further on its own.
 */
const POINTS_TO_EXCEL_WIDTH = 1 / (0.75 * 8);

/**
 * The workbook's column widths, in Excel's own units.
 *
 * Exported because the form's layout is fixed by the state — it is sized to fill a landscape page
 * exactly — so the preview has to know how wide the sheet will come out in order to fit it into the
 * panel. See `fit530PreviewColumnWidths`.
 */
export const FORM_530_COLUMN_WIDTH_UNITS: number[] = COLUMN_WIDTHS.map((points) => Math.round(points * POINTS_TO_EXCEL_WIDTH));

/**
 * How much of the preview panel's width Fortune Sheet spends on itself rather than on the sheet's
 * columns. Subtracted so the fitted columns don't cause the overflow they exist to remove.
 *
 * This is one MEASURED figure, not a derivation — the widget's layout doesn't decompose cleanly. Against
 * the rendered widget, a panel of 1063px left 892px for columns: ~45px goes outside the scrolling area
 * (the row-number gutter) and ~126px inside the column strip (the gutter again, plus roughly a spare
 * `defaultColWidth` of trailing pad past the last real column). Adding the widget's own documented
 * `rowHeaderWidth: 46` and `cellMainSrollBarSize: 12` does NOT reproduce it, so don't "simplify" this
 * into those constants.
 *
 * Rounded up from the measured 171: over-reserving costs a marginally smaller preview, while
 * under-reserving brings the horizontal scrollbar back, so this errs high on purpose.
 */
const PREVIEW_CHROME_PX = 176;

/**
 * Column widths that fit the whole form across `availableWidthPx`, or `undefined` to leave the real
 * ones alone.
 *
 * The 530's columns are sized to fill a Letter landscape page exactly (720pt), which lands around
 * 1020px on screen — wider than the preview panel on a laptop, so the form used to sit behind a
 * horizontal scrollbar with its first column off-screen. Scaling every column by one factor keeps the
 * form's proportions and shows all six columns at once. Only the PREVIEW is scaled: the workbook is
 * built from `FORM_530_COLUMN_WIDTH_UNITS` regardless, so what a facility prints is unchanged.
 *
 * Returns `undefined` when the sheet already fits — widening columns just to fill the panel would
 * misrepresent the printed form.
 */
export const fit530PreviewColumnWidths = (availableWidthPx: number): Record<string, number> | undefined => {
	const naturalPx = FORM_530_COLUMN_WIDTH_UNITS.map(charsToPx);
	const totalPx = naturalPx.reduce((total, width) => total + width, 0);
	const usablePx = availableWidthPx - PREVIEW_CHROME_PX;
	if (usablePx <= 0 || usablePx >= totalPx) return undefined;

	const scale = usablePx / totalPx;
	return Object.fromEntries(naturalPx.map((px, index) => [String(index), Math.floor(px * scale)]));
};

/**
 * The form's real column widths, in the pixel unit `excelBufferToFortuneSheet` writes into
 * `config.columnlen`. Anything rendered for PAPER must be restored to these: `resolveScalePercent`
 * never scales above 100%, so panel-fitted widths would print small with full-size text spilling out.
 */
export const form530ExportColumnWidths = (): Record<string, number> =>
	Object.fromEntries(FORM_530_COLUMN_WIDTH_UNITS.map((units, index) => [String(index), charsToPx(units)]));

const FONT = "Helvetica";
const AGENCY_FONT_SIZE = 8;
const TITLE_FONT_SIZE = 13;
const SUBTITLE_FONT_SIZE = 10;
const FIELD_FONT_SIZE = 9;
const BODY_FONT_SIZE = 9;

const HEADER_ROW_HEIGHT = 34;
/**
 * Tall enough for a wrapped two-line field label. The longest labels ("3. DIRECTOR OF NURSING/DESIGNEE",
 * "2. DATE OF PATIENT DAY (MM/DD/YY)") are wider than the column they sit in, and Excel will not spill
 * text into the merged value range beside them — so they wrap rather than getting cut in half.
 */
const FIELD_ROW_HEIGHT = 26;
const DATA_ROW_HEIGHT = 24;
const VERIFICATION_ROW_HEIGHT = 72;
const SIGNATURE_ROW_HEIGHT = 28;

const VERIFICATION_FILL = "D3D3D3";

/**
 * Pin `row` to the height Excel would auto-fit it to, but only above Excel's default font size — an
 * unsized oversized row reads as 15pt to the scale scan while Excel and the PDF renderer both grow
 * it, which costs a whole page once `fitToHeight` has no slack (APP-406). Rows at or below the
 * default stay unsized so Excel keeps auto-fitting them for facilities that edit the form.
 *
 * Only `lineHeightForFontPt` is shared with `excelWrapText.ts`: that module's full pass would also
 * force `wrapText` on every cell and reflow a state-mandated layout.
 */
const pinRowHeightForFont = (sheet: Worksheet, row: number, fontSizePt: number): void => {
	if (fontSizePt <= EXCEL_DEFAULT_FONT_SIZE_PT) return;
	sheet.getRow(row).height = lineHeightForFontPt(fontSizePt);
};

/**
 * US Letter, the official form's page size. ExcelJS's `PaperSize`
 * enum only spells out a handful of sizes and Letter isn't one of them, so the raw Excel code is cast —
 * the value itself is the standard `printerSettings` code, not a guess.
 */
const PAPER_SIZE_LETTER = 1 as ExcelJS.PaperSize;

/** The form's print margins, in inches — written to `pageSetup` below and budgeted against by
 *  `form530RowsPerPage`, so the page the roster is sliced for is the page it prints on. */
const MARGINS_IN = { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.3, footer: 0.3 };

/** US Letter, the official form's paper. Landscape is the same sheet turned, so the two sides swap. */
const LETTER_SHORT_SIDE_PT = 612;
const LETTER_LONG_SIDE_PT = 792;
const POINTS_PER_INCH = 72;

/**
 * The 530 always prints landscape, whatever the day view's own orientation setting happens to be.
 *
 * This is not a preference. The form's column widths are fixed by the state and add up to a landscape
 * page exactly; on portrait paper they are wider than the printable width, so the renderer splits the
 * form down the middle into two page-wide bands and the sheet prints two pages across. There is no
 * orientation control on the 530 tab to correct that with — it would inherit whatever the day view was
 * last set to — so the form states its own.
 */
const FORM_530_ORIENTATION = PrintoutOrientation.Landscape;

/** The page the roster is budgeted against, DERIVED from the orientation above rather than restated:
 *  a height that disagreed with the paper would slice the roster for a page it never prints on. */
const PAGE_HEIGHT_PT = FORM_530_ORIENTATION === PrintoutOrientation.Landscape ? LETTER_SHORT_SIDE_PT : LETTER_LONG_SIDE_PT;

/** A row the writers leave unsized takes Excel's default, which is what both renderers read back for it. */
const UNSIZED_ROW_HEIGHT_PT = EXCEL_DEFAULT_ROW_HEIGHT_PT;

/**
 * Rows 1-9 of `writeFormHeader`: the agency line, the title, the subtitle, a spacer, the four
 * numbered-field rows and the "7." label. Only the title and the field rows are sized; the rest take
 * Excel's default.
 */
const FORM_HEADER_HEIGHT_PT =
	UNSIZED_ROW_HEIGHT_PT +
	roundTripRowHeightPt(lineHeightForFontPt(TITLE_FONT_SIZE)) +
	UNSIZED_ROW_HEIGHT_PT +
	UNSIZED_ROW_HEIGHT_PT +
	4 * roundTripRowHeightPt(FIELD_ROW_HEIGHT) +
	UNSIZED_ROW_HEIGHT_PT;

/** `writeVerificationBlock`, plus the unsized spacer row above it. */
const VERIFICATION_BLOCK_HEIGHT_PT = UNSIZED_ROW_HEIGHT_PT + roundTripRowHeightPt(VERIFICATION_ROW_HEIGHT) + roundTripRowHeightPt(SIGNATURE_ROW_HEIGHT);

/**
 * The block Excel reprints at the top of every page: rows 1-10, the whole form header plus the column
 * headers. Declared as `printTitlesRow` below, which is what makes every page of a long shift a
 * complete, signable form rather than a bare table of names — and what makes every page's roster
 * capacity the same number.
 */
const REPEATED_HEADER_ROWS = { startRow: 1, endRow: 10 };
const REPEATED_HEADER_HEIGHT_PT = FORM_HEADER_HEIGHT_PT + roundTripRowHeightPt(HEADER_ROW_HEIGHT);

/**
 * How many roster rows fit between the repeated header and the signature block on one printed page.
 *
 * This is the number that makes the three outputs agree, and it works by ARITHMETIC rather than by any
 * instruction to break. A shift is one worksheet; its roster is written in runs of this many rows, each
 * run followed by its own signature block. The run plus the block is sized to fit the page with less
 * than one roster row to spare, so the row after it cannot fit and the page ends there — in Excel, in
 * the xlsx and in the PDF alike, because all three are reading the same row heights.
 *
 * `form530PageFitsExactly` pins the two margins that make that true.
 *
 * Only consulted when scaling is off — "scale to fit page" keeps a shift on one page and shrinks it.
 */
export const form530RowsPerPage = (): number => Math.floor(rosterCapacityPt() / roundTripRowHeightPt(DATA_ROW_HEIGHT));

const rosterCapacityPt = (): number =>
	PAGE_HEIGHT_PT - (MARGINS_IN.top + MARGINS_IN.bottom) * POINTS_PER_INCH - REPEATED_HEADER_HEIGHT_PT - VERIFICATION_BLOCK_HEIGHT_PT;

/**
 * The two clearances the page break depends on, in points — exported so a test can pin them rather than
 * re-derive the layout.
 *
 * `spare` is what is left on the page after a full run and its signature block: it must not be negative,
 * or the block is pushed onto a page of its own. `shortfall` is how far the NEXT roster row overruns
 * that leftover: it must stay positive, or that row joins the page and the block stops being the last
 * thing on it. Both are comfortable margins, not knife-edges — which is the point, since the renderers
 * accumulate these heights independently and a boundary decided by float noise is a boundary that moves.
 */
export const form530PageFitsExactly = (): { sparePt: number; shortfallPt: number } => {
	const rowPt = roundTripRowHeightPt(DATA_ROW_HEIGHT);
	const sparePt = rosterCapacityPt() - form530RowsPerPage() * rowPt;
	return { sparePt, shortfallPt: rowPt - sparePt };
};

/**
 * The page setup the 530 prints under, from the one setting the user actually has: the scale-to-fit
 * switch.
 *
 * Both axes are pinned to a single page because the form has no meaningful "fit to N by M" — it is one
 * page wide by construction, and a shift kept whole is meant to land on one page, not three. That makes
 * the switch mean what it says: on, a shift is one page; off, the shift is sliced into whole pages by
 * `form530RowsPerPage`.
 *
 * Exported because the workbook and the PDF must be handed the SAME values. The PDF is a second
 * rendering of this workbook, and a page setup resolved twice is a page setup that can disagree.
 */
export type Form530PageSetup = {
	orientation: PrintoutOrientation;
	fitToPage: boolean;
	fitToWidth: number;
	fitToHeight: number;
};

export const form530PageSetup = (fitToPage: boolean): Form530PageSetup => ({
	orientation: FORM_530_ORIENTATION,
	fitToPage,
	fitToWidth: 1,
	fitToHeight: 1,
});

const THIN_BOX = {
	top: { style: "thin" as const },
	left: { style: "thin" as const },
	bottom: { style: "thin" as const },
	right: { style: "thin" as const },
};
const UNDERLINE = { bottom: { style: "thin" as const } };

/** Merge `A{row}:{lastCol}{row}`-style ranges without hand-building the address strings. */
const mergeRow = (sheet: Worksheet, row: number, startCol: number, endCol: number): void => {
	if (endCol > startCol) sheet.mergeCells(row, startCol, row, endCol);
};

/**
 * A form field: bold label, and a value sitting on an underline that spans to `endCol` — the Excel
 * equivalent of the PDF's `labeledField`. Blank values leave a writable underlined run of cells.
 */
const labeledField = (sheet: Worksheet, row: number, labelCol: number, label: string, valueCol: number, endCol: number, value: string): void => {
	const labelCell = sheet.getCell(row, labelCol);
	labelCell.value = label;
	labelCell.font = { name: FONT, size: FIELD_FONT_SIZE, bold: true };
	labelCell.alignment = { vertical: "middle", wrapText: true };
	sheet.getRow(row).height = FIELD_ROW_HEIGHT;
	mergeRow(sheet, row, valueCol, endCol);
	const valueCell = sheet.getCell(row, valueCol);
	valueCell.value = value;
	valueCell.font = { name: FONT, size: FIELD_FONT_SIZE };
	valueCell.alignment = { vertical: "bottom" };
	valueCell.border = UNDERLINE;
	// The merge only carries the master cell's border, so the underline has to be set on every covered
	// cell for the line to actually run the width of the field.
	for (let col = valueCol + 1; col <= endCol; col++) {
		sheet.getCell(row, col).border = UNDERLINE;
	}
};

const centeredBanner = (sheet: Worksheet, row: number, text: string, size: number): void => {
	mergeRow(sheet, row, 1, COLUMN_COUNT);
	const cell = sheet.getCell(row, 1);
	cell.value = text;
	cell.font = { name: FONT, size, bold: true };
	cell.alignment = { horizontal: "center", vertical: "middle" };
	// The only rows whose font is a parameter, so the only place an unsized oversized row can appear.
	pinRowHeightForFont(sheet, row, size);
};

/** Title block + numbered fields 1-6 + the "7." table label. Returns the row the table header goes on. */
const writeFormHeader = (sheet: Worksheet, page: Page530, facility: string): number => {
	// Both agency lines are wider than one column. Merging each across its half of the sheet is what keeps
	// them whole — a bare cell would be clipped at the column edge the moment its neighbour is occupied.
	const agencyEndCol = Math.floor(COLUMN_COUNT / 2);
	mergeRow(sheet, 1, 1, agencyEndCol);
	const agency = sheet.getCell(1, 1);
	agency.value = AGENCY_LINE;
	agency.font = { name: FONT, size: AGENCY_FONT_SIZE };
	mergeRow(sheet, 1, agencyEndCol + 1, COLUMN_COUNT);
	const department = sheet.getCell(1, agencyEndCol + 1);
	department.value = DEPARTMENT_LINE;
	department.font = { name: FONT, size: AGENCY_FONT_SIZE };
	department.alignment = { horizontal: "right" };

	centeredBanner(sheet, 2, TITLE, TITLE_FONT_SIZE);
	centeredBanner(sheet, 3, SUBTITLE, SUBTITLE_FONT_SIZE);

	labeledField(sheet, 5, 1, "1. FACILITY NAME", 2, 3, facility);
	labeledField(sheet, 5, 4, "2. DATE OF PATIENT DAY (MM/DD/YY)", 6, 6, page.dateLabel);
	labeledField(sheet, 6, 1, "3. DIRECTOR OF NURSING/DESIGNEE", 2, COLUMN_COUNT, "");
	labeledField(sheet, 7, 1, "4. SHIFT (1 / 2 / 3)", 2, 3, page.shift.id);
	labeledField(sheet, 7, 4, "5. SHIFT START TIME (HH:MM AM/PM)", 6, 6, page.shift.startTimeLabel);
	labeledField(sheet, 8, 1, "6. STATION/WING/UNIT/FLOOR", 2, COLUMN_COUNT, "");

	const sectionLabel = sheet.getCell(9, 1);
	sectionLabel.value = "7.";
	sectionLabel.font = { name: FONT, size: FIELD_FONT_SIZE, bold: true };

	return 10;
};

/** The six-column table header. Row 10, and the last row of the block Excel reprints on every page. */
const writeTableHeader = (sheet: Worksheet, headerRow: number): number => {
	COLUMN_HEADERS.forEach((header, idx) => {
		const cell = sheet.getCell(headerRow, idx + 1);
		cell.value = header;
		cell.font = { name: FONT, size: BODY_FONT_SIZE, bold: true };
		cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
		cell.border = THIN_BOX;
	});
	sheet.getRow(headerRow).height = HEADER_ROW_HEIGHT;
	return headerRow + 1;
};

/** `rows` of the roster, starting at `startRow`. Returns the first row after them. */
const writeRosterRows = (sheet: Worksheet, rows: Row530[], startRow: number): number => {
	rows.forEach((row, rowIdx) => {
		const rowNumber = startRow + rowIdx;
		row.forEach((value, colIdx) => {
			const cell = sheet.getCell(rowNumber, colIdx + 1);
			cell.value = value;
			cell.font = { name: FONT, size: BODY_FONT_SIZE };
			cell.alignment = { vertical: "middle", wrapText: true };
			cell.border = THIN_BOX;
		});
		sheet.getRow(rowNumber).height = DATA_ROW_HEIGHT;
	});
	return startRow + rows.length;
};

/**
 * Field 8 — the DON verification statement and signature line, shaded as on the printed form. Returns
 * the first row after it.
 *
 * Written once per PRINTED page rather than once per sheet: it is the box the Director signs, and a
 * page of roster without one is not a 530. `FORM_REVISION` is deliberately not part of it — the print
 * footer already stamps that on every physical page, so a copy here would print twice on every page.
 */
const writeVerificationBlock = (sheet: Worksheet, startRow: number): number => {
	const shade = (row: number): void => {
		for (let col = 1; col <= COLUMN_COUNT; col++) {
			sheet.getCell(row, col).fill = { type: "pattern", pattern: "solid", fgColor: { argb: VERIFICATION_FILL } };
		}
	};

	const textRow = startRow;
	shade(textRow);
	const label = sheet.getCell(textRow, 1);
	label.value = "8.";
	label.font = { name: FONT, size: FIELD_FONT_SIZE, bold: true };
	label.alignment = { vertical: "top" };
	mergeRow(sheet, textRow, 2, COLUMN_COUNT);
	const text = sheet.getCell(textRow, 2);
	text.value = VERIFICATION_TEXT;
	text.font = { name: FONT, size: FIELD_FONT_SIZE, bold: true };
	text.alignment = { vertical: "top", wrapText: true };
	sheet.getRow(textRow).height = VERIFICATION_ROW_HEIGHT;

	const signRow = textRow + 1;
	shade(signRow);
	const mark = sheet.getCell(signRow, 3);
	mark.value = "X";
	mark.font = { name: FONT, size: FIELD_FONT_SIZE, bold: true };
	mark.alignment = { horizontal: "right", vertical: "bottom" };
	mergeRow(sheet, signRow, 4, COLUMN_COUNT);
	const signature = sheet.getCell(signRow, 4);
	signature.value = "DIRECTOR OF NURSING/DESIGNEE SIGNATURE";
	signature.font = { name: FONT, size: AGENCY_FONT_SIZE, bold: true };
	signature.alignment = { horizontal: "right", vertical: "bottom" };
	for (let col = 4; col <= COLUMN_COUNT; col++) {
		sheet.getCell(signRow, col).border = { top: { style: "thin" } };
	}
	sheet.getRow(signRow).height = SIGNATURE_ROW_HEIGHT;

	return signRow + 1;
};

/**
 * Sheet names cannot contain `/`, so the patient-day label is dashed. Shift + patient day is unique
 * across the page list by construction: a shift contributes at most one worksheet per patient day, however
 * many sheets of paper its roster runs to.
 */
const sheetName = (page: Page530): string => `Shift ${page.shift.id} ${page.dateLabel.replace(/\//g, "-")}`;

/**
 * The roster split into the runs that each end a printed page, in roster order.
 *
 * `null` keeps the roster in one run — what "scale to fit page" asks for, since the renderer then shrinks
 * the whole shift onto its single page and there is no break to put a signature block in front of.
 */
const rosterRuns = (rows: Row530[], rowsPerPage: number | null): Row530[][] => {
	if (rowsPerPage === null || rows.length <= rowsPerPage) return [rows];
	const runs: Row530[][] = [];
	for (let start = 0; start < rows.length; start += rowsPerPage) runs.push(rows.slice(start, start + rowsPerPage));
	return runs;
};

export type Create530ExcelArgs = {
	scheduledNurses: NurseInShift[];
	actualNurses: NurseInShift[];
	department: Department;
	date: DateTime;
	settings: Form530Settings;
	/** The form's own page setup — always `form530PageSetup`, so the workbook and the PDF agree. */
	pageSetup: Form530PageSetup;
	formatName: (nurse: NurseInShift) => string;
};

export const create530Excel = async ({
	scheduledNurses,
	actualNurses,
	department,
	date,
	settings,
	pageSetup,
	formatName,
}: Create530ExcelArgs): Promise<ArrayBuffer> => {
	// Scaling on keeps the whole shift on one page and lets the renderer shrink it, so there is no break
	// to place a signature block in front of. Scaling off writes the roster in page-sized runs instead.
	const rowsPerPage = pageSetup.fitToPage ? null : form530RowsPerPage();
	const pages = build530Pages({ scheduledNurses, actualNurses, date, settings, timezone: department.timezone, formatName });

	const workbook = new ExcelJS.Workbook();
	for (const page of pages) {
		const sheet = workbook.addWorksheet(sheetName(page));
		sheet.columns = FORM_530_COLUMN_WIDTH_UNITS.map((width) => ({ width }));

		const tableHeaderRow = writeFormHeader(sheet, page, department.display_name);
		let row = writeTableHeader(sheet, tableHeaderRow);
		// One run of roster per sheet of paper, each closed by its own signature block. The block is what
		// ends the page: a run plus a block leaves less room than the next roster row needs, so the page
		// break falls after the block rather than anywhere inside the roster. See `form530RowsPerPage`.
		for (const run of rosterRuns(page.rows, rowsPerPage)) {
			row = writeRosterRows(sheet, run, row);
			// The spacer row `writeVerificationBlock` is offset past keeps the shaded block off the last
			// bordered roster row, as on the printed form.
			row = writeVerificationBlock(sheet, row + 1);
		}

		sheet.pageSetup = {
			paperSize: PAPER_SIZE_LETTER,
			orientation: pageSetup.orientation === PrintoutOrientation.Portrait ? "portrait" : "landscape",
			fitToPage: pageSetup.fitToPage,
			fitToWidth: pageSetup.fitToWidth,
			fitToHeight: pageSetup.fitToHeight,
			margins: MARGINS_IN,
			horizontalCentered: true,
			// The whole form header, not just the column titles: every page of a long shift then carries the
			// facility, the patient day and the shift it belongs to, which is what makes it a 530 rather
			// than a continuation sheet of names. It also gives every page the same roster capacity.
			printTitlesRow: `${REPEATED_HEADER_ROWS.startRow}:${REPEATED_HEADER_ROWS.endRow}`,
		};
		sheet.pageSetup.printArea = `A1:${LAST_COLUMN_LETTER}${sheet.rowCount}`;
		// Mirrors the footer the PDF stamps on every physical page. A roster long enough to spill would
		// otherwise leave its continuation pages with no form revision and no page number on them at all.
		sheet.headerFooter = { oddFooter: `&L${FORM_REVISION}&R&P` };
	}

	return workbook.xlsx.writeBuffer();
};
